<template>
  <!-- Features & prices: what each module, seat and add-on costs, and how many clients use it (US 9436) -->
  <div class="adm-page">
    <section class="home-hero adm-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ $t('admin_page.eyebrow') }}</div>
        <h1 class="home-hero__title" qid="features-page-title">{{ $t('administration.features.title') }}</h1>
        <p class="home-hero__summary">{{ $t('admin_page.features.intro') }}</p>
      </div>
      <div v-if="features.length" class="home-week adm-price-sum">
        <div class="home-week__title">{{ $t('admin_page.features.sum_title') }}</div>
        <b class="adm-price-sum__value">{{ money(modulesTotal) }}</b>
        <div class="home-week__text">{{ $t('admin_page.features.sum_text', { count: paidModules }) }}</div>
      </div>
    </section>

    <section v-for="group in groups" :key="group.key" class="adm-feature-group">
      <div class="adm-feature-group__head">
        <h2>{{ $t('admin_page.features.groups.' + group.key) }}</h2>
        <span class="v2-card__sub">{{ $t('admin_page.features.groups.' + group.key + '_text') }}</span>
      </div>

      <div class="adm-features">
        <article v-for="feature in group.features"
                 :key="feature.id"
                 class="v2-card adm-feature"
                 :class="{ 'is-editing': editing && editing.id === feature.id }"
                 :qid="'feature-card-' + feature.module"
        >
          <div class="adm-feature__top">
            <span class="adm-feature__icon" :class="'is-' + group.accent"><font-awesome-icon :icon="iconFor(feature)" fixed-width/></span>
            <span class="adm-feature__titles">
              <b>{{ feature.name }}</b>
              <span v-if="usageText(feature)" class="adm-feature__usage">{{ usageText(feature) }}</span>
            </span>
          </div>

          <!-- Price, or the inline editor with its two steps: change, then confirm -->
          <div v-if="!editing || editing.id !== feature.id" class="adm-feature__price">
            <template v-if="feature.price">
              <b>{{ money(feature.price) }}</b><span>{{ $t('admin_page.features.per_month') }}</span>
            </template>
            <span v-else class="v2-tag v2-tag--teal">{{ $t('admin_page.features.included') }}</span>
            <div v-if="feature.price_yearly" class="adm-feature__yearly">{{ $t('admin_page.features.yearly', { price: money(feature.price_yearly) }) }}</div>
          </div>
          <b-form v-else class="adm-feature__edit" @submit.prevent="editing.confirm ? save() : confirm()">
            <label class="us-field-label" :for="'feature-price-' + feature.id">{{ $t('admin_page.features.new_price') }}</label>
            <div class="adm-feature__input">
              <span>€</span>
              <b-form-input :id="'feature-price-' + feature.id"
                            v-model="editing.price"
                            type="number"
                            min="0"
                            step="1"
                            required
                            :disabled="editing.confirm"
                            qid="feature-price-input"
              />
              <span>{{ $t('admin_page.features.per_month') }}</span>
            </div>
            <div class="us-note" :class="editing.confirm ? 'us-note--warn' : 'us-note--info'">
              <font-awesome-icon :icon="editing.confirm ? 'exclamation-triangle' : 'info-circle'"/>
              <span v-if="editing.confirm">{{ $t('admin_page.features.confirm_text', { from: money(feature.price), to: money(editing.price) }) }}</span>
              <span v-else>{{ $t('admin_page.features.stripe_hint') }}</span>
            </div>
            <b-alert v-if="error"
                     show
                     variant="danger"
                     class="mb-0">{{ error }}</b-alert>
            <div class="adm-feature__actions">
              <b-button size="sm"
                        variant="secondary"
                        qid="feature-cancel-button"
                        @click="editing.confirm ? editing.confirm = false : cancel()">
                {{ editing.confirm ? $t('admin_page.features.back') : $t('administration.clients.create.cancel') }}
              </b-button>
              <b-button size="sm"
                        type="submit"
                        :variant="editing.confirm ? 'danger' : 'success'"
                        :disabled="saving || String(editing.price) === String(feature.price) || editing.price === ''"
                        qid="feature-save-button"
              >
                <font-awesome-icon v-if="saving" icon="spinner" class="fa-spin mr-1"/>
                {{ editing.confirm ? $t('admin_page.features.confirm') : $t('administration.clients.create.submit') }}
              </b-button>
            </div>
          </b-form>

          <div class="adm-feature__foot">
            <code class="adm-feature__stripe" v-b-tooltip.hover :title="$t('admin_page.features.stripe_id')">{{ feature.stripe_id || '–' }}</code>
            <button v-if="feature.stripe_id"
                    type="button"
                    class="us-icon-btn"
                    v-b-tooltip.hover
                    :title="copied === feature.id ? $t('admin_page.features.copied') : $t('admin_page.features.copy')"
                    :aria-label="$t('admin_page.features.copy')"
                    qid="feature-copy-stripe"
                    @click="copy(feature)"
            >
              <font-awesome-icon :icon="copied === feature.id ? 'check' : 'copy'"/>
            </button>
            <span class="us-grow"></span>
            <button v-if="!editing || editing.id !== feature.id"
                    type="button"
                    class="us-link"
                    qid="feature-edit-button"
                    @click="edit(feature)"
            >
              <font-awesome-icon icon="pen"/>{{ $t('admin_page.features.edit_price') }}
            </button>
          </div>
        </article>
      </div>
    </section>

    <div v-if="!loading && !features.length" class="v2-card us-empty">{{ $t('system.no_results') }}</div>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import {moduleByCode} from '@/util/adminModules.js';

// Features that are priced per user, and the add-ons; everything else is a module
const SEATS = { admin: 'user-shield', validator: 'user-check', member: 'user' };
const ADD_ONS = { partition: 'layer-group' };

export default {
  name: 'Features',
  data() {
    return {
      features: [],
      loading: true,
      editing: null,
      saving: false,
      error: '',
      copied: null
    }
  },
  computed: {
    groups() {
      let byName = (a, b) => a.name.localeCompare(b.name);
      return [
        { key: 'modules', accent: 'teal', features: this.features.filter(f => !SEATS[f.module] && !ADD_ONS[f.module]).sort(byName) },
        { key: 'seats', accent: 'blue', features: this.features.filter(f => SEATS[f.module]).sort((a, b) => b.price - a.price) },
        { key: 'add_ons', accent: 'violet', features: this.features.filter(f => ADD_ONS[f.module]).sort(byName) }
      ].filter(group => group.features.length);
    },
    paidModules() {
      return this.groups.length ? this.groups[0].features.filter(f => f.price).length : 0;
    },
    modulesTotal() {
      return this.groups.length && this.groups[0].key === 'modules'
        ? this.groups[0].features.reduce((total, f) => total + (Number(f.price) || 0), 0)
        : 0;
    }
  },
  methods: {
    loadFeatures() {
      let self = this;
      piincHttp.get('features', { params: { per_page: 0, with_usage: 1 } }).then(function(response) {
        self.features = response.data.items;
        self.loading = false;
      }, function() {
        self.loading = false;
      });
    },
    iconFor(feature) {
      let module = moduleByCode(feature.module);
      return SEATS[feature.module] || ADD_ONS[feature.module] || (module && module.icon) || 'cube';
    },
    usageText(feature) {
      if (feature.usage_count === null || feature.usage_count === undefined) {
        return '';
      }
      let key = feature.module === 'partition' ? 'admin_page.features.partitions_used' : 'admin_page.features.clients_used';
      return this.$tc(key, feature.usage_count, { count: feature.usage_count });
    },
    money(value) {
      return '€' + Number(value || 0).toLocaleString(this.$i18n.locale);
    },
    edit(feature) {
      this.editing = { id: feature.id, price: feature.price, confirm: false };
      this.error = '';
    },
    cancel() {
      this.editing = null;
      this.error = '';
    },
    confirm() {
      this.editing.confirm = true;
    },
    save() {
      let self = this;
      this.saving = true;
      this.error = '';
      piincHttp.put('features/' + this.editing.id, { price: this.editing.price }).then(function() {
        self.saving = false;
        self.editing = null;
        self.$toastr('success', self.$t('administration.features.update.success'));
        self.loadFeatures();
      }, function(error) {
        self.saving = false;
        self.editing.confirm = false;
        self.error = (error.data && error.data.message) || self.$t('admin_page.error');
      });
    },
    copy(feature) {
      let self = this;
      navigator.clipboard.writeText(feature.stripe_id).then(function() {
        self.copied = feature.id;
        setTimeout(() => { self.copied = null; }, 1500);
      }, function() {});
    }
  },
  created() {
    this.loadFeatures();
  }
}
</script>
